import { useEffect, useState } from 'react'
import { useQuery } from 'react-query'
import { Link } from 'react-router-dom'
import { getRateconAttention } from 'shared/api/inbox'
import { TextV2 } from 'shared/components/Text/TextV2'
import { CompanyUser } from 'shared/context/CompanyContext'
import { TYPOGRAPHY } from 'shared/theme/typographyV2'

function countdown(milliseconds: number) {
  const seconds = Math.max(
    0,
    milliseconds > 0 ? Math.ceil(milliseconds / 1000) : Math.floor(-milliseconds / 1000)
  )
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  // Fixed digit counts keep the banner width steady while it ticks.
  const pad = (value: number) => String(value).padStart(2, '0')
  return `${hours ? `${hours}h ` : ''}${hours ? pad(minutes) : minutes}m ${pad(seconds % 60)}s`
}

export function RateconAttentionBanner({ user }: { user: CompanyUser }) {
  const allowed = Boolean(user.is_org_admin && !user.is_staff && user.organization)
  const query = useQuery(
    ['ratecon-attention', user.organization, user.id],
    async () => ({ ...(await getRateconAttention()), measuredAt: Date.now() }),
    { enabled: allowed, retry: false, refetchInterval: 15000, refetchOnWindowFocus: 'always' }
  )
  const [now, setNow] = useState(Date.now)
  const oldest = query.data?.oldest
  useEffect(() => {
    if (!allowed || !oldest) return
    setNow(Date.now())
    const timer = window.setInterval(() => setNow(Date.now()), 1000)
    return () => window.clearInterval(timer)
  }, [allowed, oldest])
  if (!allowed) return null
  // Do not keep private, previously loaded data visible after access is revoked.
  const status = (query.error as { response?: { status?: number } } | null)?.response?.status
  if (status === 401 || status === 403 || status === 409) return null
  if (!oldest || !query.data) {
    return query.isError ? (
      <Link className="text-error-d1 underline" to="/inbox/">
        Could not check rate confirmations. Open inbox
      </Link>
    ) : null
  }
  const remaining =
    Date.parse(oldest.due_at) - Date.parse(query.data.server_now) - (now - query.data.measuredAt)
  const more = query.data.count - 1
  return (
    <div
      role="region"
      aria-label="Rate confirmation attention"
      className="bg-error-d1 box-border flex max-w-full min-w-0 flex-wrap items-center gap-x-3 gap-y-0.5 rounded-lg px-3 py-1 text-white"
    >
      <Link
        to={`/inbox/?email=${encodeURIComponent(oldest.id)}`}
        className="inline-flex min-h-9 items-center text-sm font-semibold whitespace-nowrap text-white underline underline-offset-2 focus-visible:outline focus-visible:outline-2"
      >
        Rate con needs approval
      </Link>
      <TextV2 type={TYPOGRAPHY.label3} className="whitespace-nowrap text-white tabular-nums">
        {remaining > 0 ? 'Due in' : 'Overdue by'} {countdown(remaining)}
      </TextV2>
      {more > 0 && (
        <TextV2 type={TYPOGRAPHY.paragraph3} className="text-white">
          There {more === 1 ? 'is 1 more that needs' : `are ${more} more that need`} attention.
        </TextV2>
      )}
      {query.isError && (
        <TextV2 type={TYPOGRAPHY.paragraph3} className="basis-full text-white">
          Could not refresh. Showing the last known rate confirmation.
        </TextV2>
      )}
    </div>
  )
}
